import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Save, Plus, Trash2, RotateCcw, Upload, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Hotel } from '../../hooks/useHotels'
import type { CreateHotelData } from '../../services/hotelService'
import { uploadImage, uploadMultipleImages } from '../../lib/storage'
import { useLanguage } from '../../contexts/LanguageContext'

// Cities data with translations (sorted alphabetically by English name)
const CITIES = [
  { en: 'Baghdad', ar: 'بغداد', ur: 'بغداد', fa: 'بغداد' },
  { en: 'Balad', ar: 'بلد', ur: 'بلاد', fa: 'بلاد' },
  { en: 'Hllah', ar: 'حلة', ur: 'ہلہ', fa: 'حلاله' },
  { en: 'Karbala', ar: 'كربلاء', ur: 'کربلا', fa: 'کربلا' },
  { en: 'Kazmain', ar: 'كاظمين', ur: 'کاظمین', fa: 'کازمان' },
  { en: 'Kufa', ar: 'الكوفة', ur: 'کوفہ', fa: 'کوفه' },
  { en: 'Lahore', ar: 'لاهور', ur: 'لاہور', fa: 'لاهور' },
  { en: 'Mashhad', ar: 'مشهد', ur: 'مشہد', fa: 'مشهد' },
  { en: 'Musayiyab', ar: 'المسيب', ur: 'مصائب', fa: 'مسیب' },
  { en: 'Najaf', ar: 'النجف', ur: 'نجف', fa: 'نجف' },
  { en: 'Neshapur', ar: 'نيسابور', ur: 'نیشاپور', fa: 'نیشابور' },
  { en: 'Qom', ar: 'قم', ur: 'قم', fa: 'قم' },
  { en: 'Samara', ar: 'سامراء', ur: 'سامرہ', fa: 'سامره' },
  { en: 'Tehran', ar: 'طهران', ur: 'تہران', fa: 'تهران' }
]

interface HotelFormProps {
  hotel?: Hotel | null
  isOpen: boolean
  onClose: () => void
  onSave: (hotelData: CreateHotelData) => Promise<void>
  onDelete?: (hotelId: string) => Promise<void>
  onRestore?: (hotelId: string) => Promise<void>
  isLoading?: boolean
}

export function HotelForm({ hotel, isOpen, onClose, onSave, onDelete, onRestore, isLoading = false }: HotelFormProps) {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const [formData, setFormData] = useState<CreateHotelData>({
    name_en: '',
    name_ar: '',
    name_ur: '',
    name_fa: '',
    city_en: '',
    city_ar: '',
    city_ur: '',
    city_fa: '',
    address_en: '',
    address_ar: '',
    address_ur: '',
    address_fa: '',
    latitude: null,
    longitude: null,
    images: [],
    description_en: '',
    description_ar: '',
    description_ur: '',
    description_fa: '',
    phone: '',
    email: '',
    website: '',
    rating: null
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (hotel) {
      setFormData({
        name_en: hotel.name.en,
        name_ar: hotel.name.ar,
        name_ur: hotel.name.ur,
        name_fa: hotel.name.fa,
        city_en: hotel.city.en,
        city_ar: hotel.city.ar,
        city_ur: hotel.city.ur,
        city_fa: hotel.city.fa,
        address_en: hotel.address.en,
        address_ar: hotel.address.ar,
        address_ur: hotel.address.ur,
        address_fa: hotel.address.fa,
        latitude: hotel.latitude || null,
        longitude: hotel.longitude || null,
        images: hotel.images || [],
        description_en: hotel.description.en,
        description_ar: hotel.description.ar,
        description_ur: hotel.description.ur,
        description_fa: hotel.description.fa,
        phone: hotel.phone || '',
        email: hotel.email || '',
        website: hotel.website || '',
        rating: hotel.rating || null
      })
    } else {
      setFormData({
        name_en: '',
        name_ar: '',
        name_ur: '',
        name_fa: '',
        city_en: '',
        city_ar: '',
        city_ur: '',
        city_fa: '',
        address_en: '',
        address_ar: '',
        address_ur: '',
        address_fa: '',
        latitude: null,
        longitude: null,
        images: [],
        description_en: '',
        description_ar: '',
        description_ur: '',
        description_fa: '',
        phone: '',
        email: '',
        website: '',
        rating: null
      })
    }
    setErrors({})
  }, [hotel, isOpen])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name_en.trim()) newErrors.name_en = 'Name (English) is required'
    if (!formData.name_ar.trim()) newErrors.name_ar = 'Name (Arabic) is required'
    if (!formData.name_ur.trim()) newErrors.name_ur = 'Name (Urdu) is required'
    if (!formData.name_fa.trim()) newErrors.name_fa = 'Name (Persian) is required'
    if (!formData.city_en.trim()) newErrors.city_en = 'City (English) is required'
    if (!formData.city_ar.trim()) newErrors.city_ar = 'City (Arabic) is required'
    if (!formData.city_ur.trim()) newErrors.city_ur = 'City (Urdu) is required'
    if (!formData.city_fa.trim()) newErrors.city_fa = 'City (Persian) is required'
    if (!formData.address_en.trim()) newErrors.address_en = 'Address (English) is required'
    if (!formData.address_ar.trim()) newErrors.address_ar = 'Address (Arabic) is required'
    if (!formData.address_ur.trim()) newErrors.address_ur = 'Address (Urdu) is required'
    if (!formData.address_fa.trim()) newErrors.address_fa = 'Address (Persian) is required'
    if (formData.latitude === null || formData.latitude === undefined) newErrors.latitude = 'Latitude is required'
    if (formData.longitude === null || formData.longitude === undefined) newErrors.longitude = 'Longitude is required'

    if (formData.rating !== null && formData.rating !== undefined && (formData.rating < 0 || formData.rating > 5)) {
      newErrors.rating = 'Rating must be between 0 and 5'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    await onSave(formData)
  }

  const handleImageUpload = async (file: File, index: number) => {
    const uploadKey = `hotel-${index}`
    setUploading(prev => ({ ...prev, [uploadKey]: true }))
    
    const result = await uploadImage(file, 'hotels')
    
    setUploading(prev => ({ ...prev, [uploadKey]: false }))
    
    if (result.error) {
      setErrors(prev => ({ ...prev, [`image_${index}`]: result.error || 'Upload failed' }))
      return
    }
    
    if (result.url) {
      const newImages = [...(formData.images || [])]
      newImages[index] = result.url
      setFormData(prev => ({ ...prev, images: newImages }))
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[`image_${index}`]
        return newErrors
      })
    }
  }

  const handleMultipleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    setUploading(prev => ({ ...prev, 'multiple': true }))
    
    const result = await uploadMultipleImages(fileArray, 'hotels')
    
    setUploading(prev => ({ ...prev, 'multiple': false }))
    
    if (result.errors.length > 0) {
      setErrors(prev => ({ ...prev, multiple_upload: result.errors.join('; ') }))
    }
    
    if (result.urls.length > 0) {
      const newImages = [...(formData.images || []), ...result.urls]
      setFormData(prev => ({ ...prev, images: newImages }))
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.multiple_upload
        return newErrors
      })
    }
  }

  // Check if any upload is in progress
  const isUploading = Object.values(uploading).some(value => value === true)

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-2xl font-bold text-foreground">
              {hotel ? 'Edit Hotel' : 'Add New Hotel'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Hotel Name - All Languages */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Hotel Name</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Name (English) *</label>
                    <input
                      type="text"
                      value={formData.name_en}
                      onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {errors.name_en && <p className="text-sm text-red-500 mt-1">{errors.name_en}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Name (Arabic) *</label>
                    <input
                      type="text"
                      value={formData.name_ar}
                      onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.name_ar && <p className="text-sm text-red-500 mt-1">{errors.name_ar}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Name (Urdu) *</label>
                    <input
                      type="text"
                      value={formData.name_ur}
                      onChange={(e) => setFormData(prev => ({ ...prev, name_ur: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.name_ur && <p className="text-sm text-red-500 mt-1">{errors.name_ur}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Name (Persian) *</label>
                    <input
                      type="text"
                      value={formData.name_fa}
                      onChange={(e) => setFormData(prev => ({ ...prev, name_fa: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.name_fa && <p className="text-sm text-red-500 mt-1">{errors.name_fa}</p>}
                  </div>
                </div>
              </div>

              {/* City - All Languages */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">City</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">City (English) *</label>
                    <select
                      value={formData.city_en}
                      onChange={(e) => {
                        const selectedCity = CITIES.find(city => city.en === e.target.value)
                        if (selectedCity) {
                          setFormData(prev => ({
                            ...prev,
                            city_en: selectedCity.en,
                            city_ar: selectedCity.ar,
                            city_ur: selectedCity.ur,
                            city_fa: selectedCity.fa
                          }))
                        } else {
                          setFormData(prev => ({ ...prev, city_en: e.target.value }))
                        }
                      }}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select City</option>
                      {CITIES.map((city) => (
                        <option key={city.en} value={city.en}>
                          {language === 'en' ? city.en : 
                           language === 'ar' ? city.ar : 
                           language === 'ur' ? city.ur : 
                           language === 'fa' ? city.fa : city.en}
                        </option>
                      ))}
                    </select>
                    {errors.city_en && <p className="text-sm text-red-500 mt-1">{errors.city_en}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">City (Arabic) *</label>
                    <input
                      type="text"
                      value={formData.city_ar}
                      onChange={(e) => setFormData(prev => ({ ...prev, city_ar: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.city_ar && <p className="text-sm text-red-500 mt-1">{errors.city_ar}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">City (Urdu) *</label>
                    <input
                      type="text"
                      value={formData.city_ur}
                      onChange={(e) => setFormData(prev => ({ ...prev, city_ur: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.city_ur && <p className="text-sm text-red-500 mt-1">{errors.city_ur}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">City (Persian) *</label>
                    <input
                      type="text"
                      value={formData.city_fa}
                      onChange={(e) => setFormData(prev => ({ ...prev, city_fa: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.city_fa && <p className="text-sm text-red-500 mt-1">{errors.city_fa}</p>}
                  </div>
                </div>
              </div>

              {/* Address - All Languages */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Address</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Address (English) *</label>
                    <textarea
                      value={formData.address_en}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_en: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {errors.address_en && <p className="text-sm text-red-500 mt-1">{errors.address_en}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Address (Arabic) *</label>
                    <textarea
                      value={formData.address_ar}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_ar: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.address_ar && <p className="text-sm text-red-500 mt-1">{errors.address_ar}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Address (Urdu) *</label>
                    <textarea
                      value={formData.address_ur}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_ur: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.address_ur && <p className="text-sm text-red-500 mt-1">{errors.address_ur}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Address (Persian) *</label>
                    <textarea
                      value={formData.address_fa}
                      onChange={(e) => setFormData(prev => ({ ...prev, address_fa: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                    {errors.address_fa && <p className="text-sm text-red-500 mt-1">{errors.address_fa}</p>}
                  </div>
                </div>
              </div>

              {/* Location Coordinates */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Location Coordinates</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Latitude *</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.latitude || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value ? parseFloat(e.target.value) : null }))}
                      className={`w-full px-4 py-2 bg-input border ${
                        errors.latitude ? 'border-red-500' : 'border-border'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary`}
                      placeholder="e.g., 32.6167"
                    />
                    {errors.latitude && <p className="text-sm text-red-500 mt-1">{errors.latitude}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Longitude *</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.longitude || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value ? parseFloat(e.target.value) : null }))}
                      className={`w-full px-4 py-2 bg-input border ${
                        errors.longitude ? 'border-red-500' : 'border-border'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-primary`}
                      placeholder="e.g., 44.0244"
                    />
                    {errors.longitude && <p className="text-sm text-red-500 mt-1">{errors.longitude}</p>}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Contact Information (Optional)</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Rating (0-5, Optional)</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.rating || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, rating: e.target.value ? parseFloat(e.target.value) : null }))}
                  className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {errors.rating && <p className="text-sm text-red-500 mt-1">{errors.rating}</p>}
              </div>

              {/* Description - All Languages */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Description (Optional)</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Description (English)</label>
                    <textarea
                      value={formData.description_en}
                      onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Description (Arabic)</label>
                    <textarea
                      value={formData.description_ar}
                      onChange={(e) => setFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Description (Urdu)</label>
                    <textarea
                      value={formData.description_ur}
                      onChange={(e) => setFormData(prev => ({ ...prev, description_ur: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Description (Persian)</label>
                    <textarea
                      value={formData.description_fa}
                      onChange={(e) => setFormData(prev => ({ ...prev, description_fa: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Hotel Images (Max 2MB each)</h3>
                
                {/* Multiple Image Upload */}
                <div className="mb-4">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    multiple
                    onChange={(e) => handleMultipleImageUpload(e.target.files)}
                    className="hidden"
                    id="hotel-multiple-images"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="hotel-multiple-images"
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-block"
                  >
                    {uploading['multiple'] ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Upload Multiple Images
                      </>
                    )}
                  </label>
                  {errors.multiple_upload && (
                    <p className="text-sm text-red-500 mt-2">{errors.multiple_upload}</p>
                  )}
                </div>

                <div className="space-y-2">
                  {formData.images?.map((image, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 border border-border rounded-lg">
                      {image ? (
                        <>
                          <img 
                            src={image} 
                            alt={`Hotel image ${index + 1}`} 
                            className="h-16 w-16 object-cover rounded-lg border border-border"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-muted-foreground truncate">{image}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.images?.filter((_, i) => i !== index) || []
                              setFormData(prev => ({ ...prev, images: newImages }))
                            }}
                            className="p-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                handleImageUpload(file, index)
                              }
                            }}
                            className="hidden"
                            id={`hotel-image-${index}`}
                          />
                          <label
                            htmlFor={`hotel-image-${index}`}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {uploading[`hotel-${index}`] ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4" />
                                Upload Image
                              </>
                            )}
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.images?.filter((_, i) => i !== index) || []
                              setFormData(prev => ({ ...prev, images: newImages }))
                            }}
                            className="p-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {errors[`image_${index}`] && (
                        <p className="text-sm text-red-500">{errors[`image_${index}`]}</p>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, images: [...(prev.images || []), ''] }))}
                    className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
                  >
                    <Plus className="h-4 w-4" />
                    Add Hotel Image
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border">
            {hotel && (
              <div className="flex items-center gap-2">
                {hotel.deletedAt ? (
                  <button
                    type="button"
                    onClick={async () => {
                      if (onRestore && hotel.id) {
                        await onRestore(hotel.id)
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Restore Hotel
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (onDelete && hotel.id) {
                        await onDelete(hotel.id)
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete Hotel
                  </button>
                )}
              </div>
            )}
            
            <div className="flex items-center gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isLoading || isUploading}
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading images...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Hotel
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

